import { useState } from 'react';
import { 
  Pill, 
  Send, 
  CheckCircle2, 
  Circle,
  ChevronRight,
  Copy,
  ExternalLink,
  X,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingSteps = ({ 
  famiglia, 
  hasMedicines, 
  onGoToMedicines,
  onGoToLink 
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [copying, setCopying] = useState(false);

  // Non mostrare se l'utente ha già completato tutto o ha chiuso
  const localDismissed = localStorage.getItem(`onboarding_dismissed_${famiglia?.id}`);
  if (dismissed || localDismissed) return null;

  const assistantLink = famiglia?.token ? `${window.location.origin}/app/${famiglia.token}` : null;
  
  // Step status
  const step1Complete = hasMedicines;
  const step2Complete = false; // Non possiamo sapere se ha mandato il link, quindi sempre visibile

  const handleCopyLink = async () => {
    if (!assistantLink) return;
    setCopying(true);
    try {
      await navigator.clipboard.writeText(assistantLink);
      toast.success('Link copiato! Mandalo alla tua assistente');
    } catch (err) {
      toast.error('Errore nella copia');
    }
    setCopying(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(`onboarding_dismissed_${famiglia?.id}`, 'true');
  };

  return (
    <div className="bg-gradient-to-r from-[#4A7C59]/10 to-[#5DADE2]/10 rounded-2xl p-5 mb-6 border-2 border-[#4A7C59]/20 relative">
      {/* Close button */}
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 hover:bg-white/50 rounded-full transition-colors"
        title="Nascondi guida"
      >
        <X className="w-4 h-4 text-gray-500" />
      </button>

      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-[#4A7C59]" />
        <h3 className="font-bold text-[#2C3E50]">Primi Passi</h3>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {/* Step 1 */}
        <div 
          className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${
            step1Complete 
              ? 'bg-emerald-50 border border-emerald-200' 
              : 'bg-white border-2 border-[#4A7C59] shadow-sm hover:shadow-md'
          }`}
          onClick={onGoToMedicines}
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            step1Complete ? 'bg-emerald-500' : 'bg-[#4A7C59]'
          }`}>
            {step1Complete ? (
              <CheckCircle2 className="w-5 h-5 text-white" />
            ) : (
              <span className="text-white font-bold text-sm">1</span>
            )}
          </div>
          <div className="flex-1">
            <p className={`font-semibold ${step1Complete ? 'text-emerald-700' : 'text-[#2C3E50]'}`}>
              {step1Complete ? 'Farmaci configurati!' : 'Configura i farmaci'}
            </p>
            <p className="text-xs text-gray-500">
              {step1Complete 
                ? 'Puoi sempre modificarli dalla sezione Farmaci' 
                : 'Aggiungi le medicine da somministrare'}
            </p>
          </div>
          {!step1Complete && (
            <ChevronRight className="w-5 h-5 text-[#4A7C59]" />
          )}
        </div>

        {/* Arrow between steps */}
        <div className="flex justify-center">
          <div className="w-0.5 h-4 bg-gray-300"></div>
        </div>

        {/* Step 2 */}
        <div className={`p-3 rounded-xl transition-all ${
          step1Complete 
            ? 'bg-white border-2 border-[#4A7C59] shadow-sm' 
            : 'bg-gray-50 border border-gray-200 opacity-60'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              step1Complete ? 'bg-[#4A7C59]' : 'bg-gray-300'
            }`}>
              <span className="text-white font-bold text-sm">2</span>
            </div>
            <div className="flex-1">
              <p className={`font-semibold ${step1Complete ? 'text-[#2C3E50]' : 'text-gray-400'}`}>
                Manda il link all'assistente
              </p>
              <p className="text-xs text-gray-500">
                Condividi questo link per farla accedere all'app
              </p>
            </div>
          </div>

          {/* Link actions - only if step 1 complete */}
          {step1Complete && assistantLink && (
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleCopyLink}
                disabled={copying}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4A7C59] text-white rounded-xl font-medium hover:bg-[#3d6549] transition-colors"
              >
                <Copy className="w-4 h-4" />
                {copying ? 'Copiato!' : 'Copia Link'}
              </button>
              <button
                onClick={onGoToLink}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Vedi QR Code
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer note */}
      <div className="mt-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
        <p className="text-xs text-amber-700">
          <strong>💡 Nota:</strong> Dopo aver mandato il link, riceverai le notifiche ogni volta che l'assistente registra un'attività.
          Se modifichi i farmaci, ricordati di avvisare l'assistente!
        </p>
      </div>
    </div>
  );
};

export default OnboardingSteps;
